import { useContext, useMemo } from 'react';
import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@ltvco/refresh-lib/vendors';
import { enqueueSnackbar, useServiceToken } from '@ltvco/refresh-lib/v1';
import { ResponseError } from '@ltvco/refresh-lib/utils';
import {
  CommunityBuzzBootstrapParams,
  CommunityBuzzBootstrapResponse,
  CommunityBuzzLLMParams,
  CommunityBuzzLLMResponse,
  CreateAnswerParams,
  CreateQuestionParams,
  GetQuestionsParams,
} from 'api/requests/communityBuzz/interfaces';
import { communityBuzzRequests } from 'api/requests/communityBuzz/communityBuzz';

export interface CommunityBuzzParams {
  questionsListParams: GetQuestionsParams;
  questionId?: number;
  llmParams?: {
    year: string | number;
    make: string;
    model: string;
    trim: string;
  };
}

export const useCommunityBuzz = ({
  questionsListParams,
  questionId,
  llmParams,
}: CommunityBuzzParams) => {
  const { logError } = useContext(AppConfig);
  const { data: oramTokenData } = useServiceToken('oram');
  const { data: llmTokenData } = useServiceToken('llm');
  const queryClient = useQueryClient();
  const hasQuestionsParams =
    Boolean(questionsListParams?.year) &&
    Boolean(questionsListParams?.make) &&
    Boolean(questionsListParams?.model) &&
    Boolean(questionsListParams?.trim);

  const questionsListQuery = useQuery({
    queryKey: [
      'community-buzz',
      'questions',
      questionsListParams,
      oramTokenData?.token ?? 'public',
    ],
    queryFn: () =>
      communityBuzzRequests.getQuestions(
        questionsListParams,
        oramTokenData?.token,
      ),
    enabled: hasQuestionsParams,
    refetchOnMount: true,
    onError: (err: Error) => {
      enqueueSnackbar('Loading community buzz questions, please wait', {
        variant: 'info',
      });
      logError('Failed to fetch community buzz questions', err as Error);
    },
  });

  const questionQuery = useQuery({
    queryKey: [
      'community-buzz',
      'question',
      questionId,
      oramTokenData?.token ?? 'public',
    ],
    queryFn: () =>
      communityBuzzRequests.getQuestion(
        { id: questionId as number },
        oramTokenData?.token,
      ),
    enabled: Boolean(questionId),
  });

  const createQuestionMutation = useMutation({
    mutationFn: (params: CreateQuestionParams) => {
      if (!oramTokenData?.token) {
        throw new Error('Missing service token for community buzz question.');
      }

      return communityBuzzRequests.createQuestion(params, oramTokenData.token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['community-buzz', 'questions', questionsListParams],
      });
    },
    onError: (err: Error) => {
      // If the content moderation failed, we don't want to show an error message
      // because the user will be notified through the QAEntryModal
      if ((err as ResponseError).response?.status !== 422) {
        enqueueSnackbar(
          'Error submitting a community buzz question. Please try again.',
          {
            variant: 'error',
          },
        );
        logError('Failed to submit a community buzz question', err as Error);
      }
    },
  });

  const createAnswerMutation = useMutation({
    mutationFn: (params: CreateAnswerParams) => {
      if (!oramTokenData?.token) {
        throw new Error('Missing service token for community buzz answer.');
      }

      return communityBuzzRequests.createAnswer(params, oramTokenData.token);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['community-buzz', 'question', variables.question_id],
      });
    },
    onError: (err: Error) => {
      // If the content moderation failed, we don't want to show an error message
      // because the user will be notified through the QAEntryModal
      if ((err as ResponseError).response?.status !== 422) {
        enqueueSnackbar(
          'Error submitting a community buzz answer. Please try again.',
          {
            variant: 'error',
          },
        );
        logError('Failed to submit a community buzz answer', err as Error);
      }
    },
  });

  const normalizeYear = (value: string | number) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return NaN;
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : NaN;
    }
    return NaN;
  };

  const rawLLMParams = llmParams || {
    year: questionsListParams.year,
    make: questionsListParams.make,
    model: questionsListParams.model,
    trim: questionsListParams.trim,
  };

  const normalizedLLMParams: CommunityBuzzLLMParams = useMemo(
    () => ({
      year: normalizeYear(rawLLMParams.year),
      make: rawLLMParams.make?.trim() || '',
      model: rawLLMParams.model?.trim() || '',
      trim: rawLLMParams.trim?.trim() || '',
    }),
    [rawLLMParams],
  );

  const llmGenerationMutation = useMutation<CommunityBuzzLLMResponse, Error>({
    mutationFn: () => {
      if (!llmTokenData?.token) {
        throw new Error('Missing LLM token');
      }

      if (!Number.isFinite(normalizedLLMParams.year)) {
        throw new Error('Invalid year provided for Community Buzz generation.');
      }

      return communityBuzzRequests.generateCommunityBuzz(
        normalizedLLMParams,
        llmTokenData.token,
      );
    },
    onError: (error) => {
      logError('Error fetching community buzz content', error as Error);
    },
  });

  const baseBootstrapParams: Omit<CommunityBuzzBootstrapParams, 'data'> = {
    year: questionsListParams.year,
    make: questionsListParams.make,
    model: questionsListParams.model,
    trim: questionsListParams.trim,
  };

  const bootstrapMutation = useMutation<
    CommunityBuzzBootstrapResponse,
    Error,
    CommunityBuzzBootstrapParams['data']
  >({
    mutationFn: (data) => {
      if (!oramTokenData?.token) {
        throw new Error('Missing service token for community buzz bootstrap');
      }

      return communityBuzzRequests.bootstrapCommunityBuzzQuestions(
        {
          ...baseBootstrapParams,
          data,
        },
        oramTokenData.token,
      );
    },
    onError: (error) => {
      logError('Error bootstrapping community buzz questions', error as Error);
    },
  });

  return {
    questionsListQuery,
    questionQuery,
    createQuestionMutation,
    createAnswerMutation,
    llmGenerationMutation,
    llmTokenReady: Boolean(llmTokenData?.token),
    bootstrapMutation,
    bootstrapTokenReady: Boolean(oramTokenData?.token),
  };
};
